/**
 * Cart Line Item Variant Swap (Skip 2026-05-08, hardened 2026-05-22 post-Cecilia review)
 *
 * Wraps the inline <select> rendered for single-option, non-subscription
 * cart line items. On change, swaps the line's variant via a remove+add
 * sequence (Shopify Cart Ajax has no per-line variant-swap primitive).
 *
 * Out-of-scope by design (preserves cart safety):
 *   - Subscription lines (selling_plan_allocation != nil) — Liquid omits
 *     the editor; avoids breaking ReCharge recurring charges.
 *   - Multi-option products (Color × Size) — Liquid omits the editor.
 *   - Sold-out variants → option is `disabled` in Liquid.
 *
 * Edge cases handled (post Cecilia 2026-05-22):
 *   - Double-click / rapid-fire change events: re-entrancy guarded via
 *     in-flight flag; originalValue updated immediately on user choice so
 *     a queued second change-event no-ops against the same-value check.
 *   - Step-2 (add) fails after step-1 (remove) succeeds: attempts to
 *     restore the original line so we don't silently lose inventory.
 *   - Stale data-quantity (qty changed via Dawn +/- buttons): qty read
 *     from the line's actual quantity input at click time, not the
 *     cached dataset.
 *   - cartUpdate publish fires AFTER renderContents so subscribers
 *     receive up-to-date DOM line indices.
 */

// Module-scoped flag for cross-line concurrency guard (Clare #7).
// Prevents Step 1 of swap A from racing against Step 1 of swap B on a
// DIFFERENT line — Shopify re-indexes lines after a remove, so a second
// concurrent swap captured pre-removal line index would target the wrong
// line. Per-element disable is not enough.
let __swapInFlightGlobal = false;

class CartVariantSwap extends HTMLElement {
  connectedCallback() {
    const select = this.querySelector('.cart-item__variant-select');
    if (!select || select._wired) return;
    select._wired = true;

    // Cache the original value so we can revert on failure.
    select.dataset.originalValue = select.value;
    this._inFlight = false;

    select.addEventListener('change', async () => {
      // Cecilia #2 + Clare #7 — guard against (a) double-events on this select
      // and (b) concurrent swaps on other lines that would race against line
      // re-indexing.
      if (this._inFlight || __swapInFlightGlobal) return;

      const newVariantId = parseInt(select.value, 10);
      const oldVariantId = parseInt(select.dataset.originalValue, 10);
      const line = parseInt(select.dataset.line, 10);
      // Cecilia #4 — read qty from the actual qty input on this row, not from
      // the cached dataset which can be stale if the customer adjusted qty
      // via Dawn's +/- between page load and variant change.
      const quantity = this._currentLineQuantity();
      if (!newVariantId || !oldVariantId || !line || !quantity || newVariantId === oldVariantId) {
        return;
      }

      this._inFlight = true;
      __swapInFlightGlobal = true;
      select.dataset.originalValue = String(newVariantId);
      select.disabled = true;
      this.classList.add('is-loading');

      let step1Succeeded = false;

      try {
        /* Two-step remove+add. Shopify's Cart Ajax API has no per-line
           variant-swap operation. Background: /cart/change.js with `line+id`
           returns 400 ("expected Integer to be a regex... :id"); /cart/update.js
           with variant-id map zeroes ALL lines of the source variant and
           REPLACES the new variant's existing qty (consolidation data loss).
           Remove + add by line index is the only line-specific, consolidation-
           safe pattern. */

        // Step 1 — remove the source line.
        const removeResp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: this._headers(),
          body: JSON.stringify({ line, quantity: 0 }),
        });
        if (!removeResp.ok) {
          const errText = await removeResp.text();
          throw new Error(`Remove line ${line} failed: ${errText.slice(0, 120)}`);
        }
        step1Succeeded = true;

        // Step 2 — add the new variant.
        const addResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: this._headers(),
          body: JSON.stringify({
            items: [{ id: newVariantId, quantity }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: '/cart',
          }),
        });
        const parsedState = await addResp.json();
        if (!addResp.ok || (parsedState.status && Number(parsedState.status) >= 400)) {
          throw new Error(parsedState.message || parsedState.description || 'Variant add failed');
        }

        // Re-render the drawer FIRST so DOM line indices are up to date when
        // subscribers receive the cartUpdate event (Cecilia #1).
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(parsedState);
        }

        // Notify other listeners (Dawn-idiomatic). The /cart/add.js items-
        // array response doesn't include `item_count` at the top level
        // (Clare #5), so we re-fetch /cart.js to give subscribers a complete
        // cart shape. Skipped if no subscribers wired (no global publish).
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            const freshCart = await fetch('/cart.js', { headers: this._headers() }).then((r) => r.json());
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-variant-swap',
              productVariantId: newVariantId,
              cartData: freshCart,
            });
          } catch (publishErr) {
            // Non-fatal — drawer is re-rendered, customer sees correct UI.
            console.warn('[cart-variant-swap] publish skipped:', publishErr);
          }
        }

        __swapInFlightGlobal = false;
        // Element will be destroyed by renderContents; no need to clear
        // _inFlight or re-enable select here.
      } catch (err) {
        console.error('[cart-variant-swap]', err);

        // Cecilia #3 — if step 1 succeeded but step 2 failed we just removed
        // the customer's line. Attempt to restore the original variant at
        // the original quantity so they don't silently lose inventory.
        if (step1Succeeded) {
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: this._headers(),
              body: JSON.stringify({ items: [{ id: oldVariantId, quantity }] }),
            });
            // Refresh drawer to reflect restored state.
            const drawer = document.querySelector('cart-drawer');
            const sectionResp = await fetch('/?sections=cart-drawer').then((r) => r.json());
            if (drawer && sectionResp['cart-drawer']) {
              drawer.renderContents({ sections: sectionResp });
            }
          } catch (restoreErr) {
            console.error('[cart-variant-swap] restore-after-add-failure also failed', restoreErr);
            // Last resort: reload the page to resync visible cart with server state.
            // Better to interrupt the customer than to leave them with a missing line.
            window.location.reload();
            return;
          }
        }

        // Revert visible state.
        select.value = select.dataset.originalValue = String(oldVariantId);
        select.disabled = false;
        this.classList.remove('is-loading');
        this._inFlight = false;
        __swapInFlightGlobal = false;
        this._showError(err.message || 'Could not change variant');
      }
    });
  }

  _headers() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
  }

  /**
   * Read the qty from the actual qty input on this cart line. Falls back to
   * dataset.quantity (Liquid-rendered) then to 1. Reading DOM at click time
   * survives Dawn's +/- qty updates that don't re-render the whole drawer.
   */
  _currentLineQuantity() {
    const row = this.closest('.cart-item') || this.closest('tr');
    const input = row && row.querySelector('input.quantity__input, input[name="updates[]"]');
    const fromInput = input && parseInt(input.value, 10);
    if (fromInput && fromInput > 0) return fromInput;
    const fromDataset = parseInt(this.dataset.quantity, 10);
    if (fromDataset && fromDataset > 0) return fromDataset;
    return 1;
  }

  /**
   * Surface a brief inline error near the select (Cecilia #8 — sold-out or
   * other Shopify rejection should not be a silent revert).
   */
  _showError(message) {
    // Avoid stacking duplicates.
    const existing = this.querySelector('.cart-item__variant-error');
    if (existing) existing.remove();

    const el = document.createElement('p');
    el.className = 'cart-item__variant-error';
    el.setAttribute('role', 'alert');
    el.style.cssText =
      'margin: 0.4rem 0 0; font-size: 1.1rem; line-height: 1.3; color: #B12704;';
    el.textContent = message;
    this.appendChild(el);
    setTimeout(() => el.remove(), 5000);
  }
}

if (!customElements.get('cart-variant-swap')) {
  customElements.define('cart-variant-swap', CartVariantSwap);
}
